import { useCallback, useEffect, useRef, useState } from 'react'
import type { MouseEvent } from 'react'
import type { DashboardPayload } from '../types/payload'
import { Header } from './Header'
import { IndiceCesta } from './sections/IndiceCesta'
import { OverviewPanel } from './sections/OverviewPanel'
import { QuantidadeMP } from './sections/QuantidadeMP'
import { RupturaDigital } from './sections/RupturaDigital'
import { VariacaoPrecos } from './sections/VariacaoPrecos'
import { downloadStandaloneHtml } from '../utils/exportToStandaloneHtml'
import logoAbmapro from '../../assets/imagens/logoabmapro.png'
import logoMi7 from '../../assets/imagens/logomi7.png'
import styles from './Dashboard.module.css'

interface DashboardProps {
  payload: DashboardPayload
  onEditionChange?: (period: string) => void
}

const mi7ScaleCards = [
  { value: '+850', label: 'lojas monitoradas no Brasil' },
  { value: '+370', label: 'cidades cobertas' },
  { value: '+1.500', label: 'sites, apps e plataformas' },
  { value: '+1,9 Bilhão', label: 'de produtos pesquisados por ano' },
  { value: '+7 Milhões', label: 'de itens auditados diariamente' },
  { value: '+75', label: 'plataformas ativas entre marketplaces, e-commerce e físicas' },
]

const mi7Objectives = [
  'Transparência de Mercado',
  'Inteligência Competitiva',
  'Desenvolvimento Setorial',
  'Benchmarking',
]

const mi7Contacts = [
  { kind: 'social', label: 'Redes Sociais', value: '@grupomi7' },
  { kind: 'whatsapp', label: 'WhatsApp', value: '11 95834-3444' },
  { kind: 'email', label: 'E-mail', value: 'kleberthon.grenzi@grupomi7.io' },
  { kind: 'site', label: 'Site', value: 'www.grupomi7.com.br' },
] as const

function Mi7ContactIcon({ kind }: { kind: (typeof mi7Contacts)[number]['kind'] }) {
  switch (kind) {
    case 'social':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M7 8.5A4.5 4.5 0 0 1 11.5 4H16v4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17 8.5A4.5 4.5 0 0 1 12.5 13H8V8.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7 15.5A4.5 4.5 0 0 0 11.5 20H16v-4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case 'whatsapp':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 20a8 8 0 1 0-4.2-1.2L4 20l1.3-3.6A8 8 0 0 0 12 20Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.6 9.2c.2-.5.5-.5.8-.5h.4c.2 0 .4 0 .5.4l.6 1.3c.1.2.1.4 0 .6l-.4.5c-.1.1-.1.3 0 .4.3.6.8 1 1.4 1.3.1.1.3.1.4 0l.5-.4c.2-.1.4-.1.6 0l1.3.6c.3.1.4.3.4.5v.4c0 .3 0 .6-.5.8-.4.2-.8.3-1.2.2A7 7 0 0 1 9.9 13c-.8-.8-1.3-1.8-1.5-2.9-.1-.3 0-.7.2-.9Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case 'email':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect
            x="4"
            y="6"
            width="16"
            height="12"
            rx="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="m5 8 7 5 7-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case 'site':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle
            cx="12"
            cy="12"
            r="8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 12h16M12 4a13.3 13.3 0 0 1 0 16M12 4a13.3 13.3 0 0 0 0 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
  }
}

export function Dashboard({ payload, onEditionChange }: DashboardProps) {
  const { meta, sections, insights, ctas } = payload
  const [activeSection, setActiveSection] = useState('resumo-executivo')
  const [isMi7ModalOpen, setIsMi7ModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const scrollPaneRef = useRef<HTMLElement | null>(null)

  // Imperative (not a useEffect on activeSection, which the scrollspy below
  // also drives while the user scrolls — reacting to that would fight the
  // user's own scroll). scrollIntoView is deferred one frame past the state
  // update so it reads post-render layout, instead of relying on the browser's
  // native anchor-jump timing, which can land short if content above the
  // target (charts, images) hasn't finished laying out yet.
  const handleSidebarNavigate = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, sectionId: string) => {
      event.preventDefault()
      setActiveSection(sectionId)

      requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'instant', block: 'start' })
      })
    },
    [],
  )

  const handleExport = useCallback(async () => {
    setIsExporting(true)

    try {
      await downloadStandaloneHtml(payload)
    } catch (error) {
      console.error('Falha ao exportar a edição em HTML autônomo.', error)
      window.alert(error instanceof Error ? error.message : 'Falha ao exportar a edição em HTML autônomo.')
    } finally {
      setIsExporting(false)
    }
  }, [payload])

  const handleCopyLink = useCallback(async () => {
    const shareUrl = `https://inmp.pages.dev/?edicion=${payload.meta.period}`

    try {
      await navigator.clipboard.writeText(shareUrl)
    } catch (error) {
      console.error('Falha ao copiar o link da edição.', error)
      window.prompt('Copie o link da edição abaixo:', shareUrl)
    }
  }, [payload.meta.period])

  const sidebarSections = [
    {
      id: 'resumo-executivo',
      title: 'Resumo Executivo',
      description: 'Leitura institucional da edição e KPIs de entrada.',
    },
    {
      id: 'quantidade-mp',
      title: 'Quantidade MP',
      description: 'Presença por canal e rede monitorada.',
    },
    {
      id: 'variacao-precos',
      title: 'Variação de Preços',
      description: 'Ano móvel e tendência por departamento.',
    },
    {
      id: 'indice-cesta',
      title: 'Índice de Cesta',
      description: 'Densidade por faixa e leitura semanal.',
    },
    {
      id: 'ruptura-digital',
      title: 'Ruptura Digital',
      description: 'Medianas gerais e de MP por canal.',
    },
    {
      id: 'conheca-mi7',
      title: 'Conheça a Mi7',
      description: 'Infraestrutura tecnológica e objetivos da parceria.',
    },
  ]

  useEffect(() => {
    const root = scrollPaneRef.current

    if (!root) {
      return
    }

    const sectionElements = Array.from(
      root.querySelectorAll<HTMLElement>(`.${styles.anchorSection}[id]`),
    )

    if (sectionElements.length === 0) {
      return
    }

    let frameId = 0

    // Viewport-relative, not root.scrollTop-relative: the page itself
    // scrolls now (desktop and mobile both use natural document flow — see
    // .shell/.scrollPane in Dashboard.module.css), so .scrollPane no longer
    // has an independent scroll position to track.
    const updateActiveFromScrollPosition = () => {
      frameId = 0

      const lastSection = sectionElements[sectionElements.length - 1]

      // Hard guarantee for the last section: short sections (e.g. "Conheça
      // a Mi7") can end well before the page's true scroll-bottom once
      // there's a footer/whitespace tail, so "is this section's top past
      // the activation line" alone can leave the second-to-last section
      // stuck active even once the user has scrolled all the way down.
      // Near-bottom-of-document wins outright, regardless of section height.
      const nearDocumentBottom = window.innerHeight + window.scrollY
        >= document.documentElement.scrollHeight - 2

      if (lastSection && nearDocumentBottom) {
        setActiveSection(current => (current === lastSection.id ? current : lastSection.id))
        return
      }

      const activationOffset = Math.min(window.innerHeight * 0.18, 160)
      let nextActive = sectionElements[0]?.id ?? 'resumo-executivo'

      for (const section of sectionElements) {
        const sectionTop = section.getBoundingClientRect().top

        if (sectionTop <= activationOffset) {
          nextActive = section.id
          continue
        }

        break
      }

      if (nextActive) {
        setActiveSection(current => (current === nextActive ? current : nextActive))
      }
    }

    const scheduleUpdate = () => {
      if (frameId !== 0) {
        return
      }

      frameId = window.requestAnimationFrame(updateActiveFromScrollPosition)
    }

    // root: unset (defaults to the viewport) — .scrollPane isn't a
    // clipping/scrolling ancestor anymore, so it can't serve as the
    // intersection root. rootMargin shrinks the effective viewport to a
    // band from 40% down to 45% up (a 15%-tall strip past the middle),
    // matching where updateActiveFromScrollPosition's own activation line
    // sits — entries firing there is what re-triggers scheduleUpdate as
    // each section's boundary crosses it while scrolling.
    const observer = new IntersectionObserver(
      () => {
        scheduleUpdate()
      },
      {
        threshold: [0, 0.15, 0.35, 0.6, 0.85],
        rootMargin: '-40% 0px -55% 0px',
      },
    )

    sectionElements.forEach(section => observer.observe(section))
    window.addEventListener('scroll', scheduleUpdate, { passive: true })
    window.addEventListener('resize', scheduleUpdate)
    scheduleUpdate()

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', scheduleUpdate)
      window.removeEventListener('resize', scheduleUpdate)

      if (frameId !== 0) {
        window.cancelAnimationFrame(frameId)
      }
    }
  }, [payload.meta.period])

  useEffect(() => {
    if (!isMi7ModalOpen) {
      return
    }

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMi7ModalOpen(false)
      }
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isMi7ModalOpen])

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarBrand}>
          <div className={styles.mi7BrandBlock}>
            <img src={logoMi7} alt="Grupo Mi7" className={styles.logoMi7} />
          </div>
          <div className={styles.sidebarTitleBlock}>
            <h1 className={styles.sidebarTitle}>Portal INMP</h1>
            <p className={styles.sidebarSubtitle}>Índice Nacional de Marca Própria</p>
          </div>
          <img src={logoAbmapro} alt="ABMAPRO" className={styles.logoAbmapro} />
          <span className={styles.sidebarBadge}>{meta.edition_label}</span>
        </div>

        <nav className={styles.sidebarNav} aria-label="Navegação do dashboard">
          {sidebarSections.map(section => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={`${styles.sidebarLink} ${section.id === activeSection ? styles.active : ''}`}
              aria-current={section.id === activeSection ? 'location' : undefined}
              onClick={event => handleSidebarNavigate(event, section.id)}
            >
              <span className={styles.sidebarLinkTitle}>{section.title}</span>
              <small className={styles.sidebarLinkText}>{section.description}</small>
            </a>
          ))}
        </nav>
      </aside>

      <div className={styles.workspace}>
        <Header
          meta={meta}
          navigation={payload.edition_navigation}
          onEditionChange={onEditionChange}
          onExport={handleExport}
          isExporting={isExporting}
          onCopyLink={handleCopyLink}
        />

        <main ref={scrollPaneRef} className={styles.scrollPane}>
          <div className={styles.workspaceInner}>
            <div className={styles.content}>
              <div className={styles.panelStack}>
                <div id="resumo-executivo" className={styles.anchorSection}>
                  <OverviewPanel
                    insights={insights}
                    quantidadeMpTotals={sections.quantidade_mp.totals}
                    variacaoPrecos={sections.variacao_precos}
                    rupturaDigital={sections.ruptura_digital}
                    editorial={payload.editorial}
                  />
                </div>

                <div id="quantidade-mp" className={styles.anchorSection}>
                  <QuantidadeMP
                    section={sections.quantidade_mp}
                    ctas={ctas.filter(cta => cta.section === 'quantidade_mp')}
                    onCtaClick={() => undefined}
                  />
                </div>

                <div id="variacao-precos" className={styles.anchorSection}>
                  <VariacaoPrecos
                    section={sections.variacao_precos}
                    ctas={ctas.filter(cta => cta.section === 'variacao_precos')}
                    onCtaClick={() => undefined}
                  />
                </div>

                <div id="indice-cesta" className={styles.anchorSection}>
                  <IndiceCesta section={sections.indice_cesta} />
                </div>

                <div id="ruptura-digital" className={styles.anchorSection}>
                  <RupturaDigital
                    section={sections.ruptura_digital}
                    ctas={ctas.filter(cta => cta.section === 'ruptura_digital')}
                    onCtaClick={() => undefined}
                  />
                </div>

                <div id="conheca-mi7" className={styles.anchorSection}>
                  <section className={styles.mi7Teaser}>
                    <div className={styles.mi7TeaserHeader}>
                      <div>
                        <span className={styles.mi7Eyebrow}>Conheça a Mi7</span>
                        <h2 className={styles.mi7Title}>
                          Grupo Mi7 — Inteligência & Tecnologia de Dados
                        </h2>
                      </div>
                    </div>

                    <p className={styles.mi7Lead}>
                      O Grupo Mi7 sustenta a leitura de mercado do INMP com uma infraestrutura
                      proprietária de captura, auditoria e inteligência de dados em escala
                      nacional, conectando varejo físico, digital e ecossistemas de marketplace.
                    </p>

                    <div className={styles.mi7ScaleGrid}>
                      {mi7ScaleCards.map(card => (
                        <article key={card.label} className={styles.mi7ScaleCard}>
                          <strong className={styles.mi7ScaleValue}>{card.value}</strong>
                          <span className={styles.mi7ScaleLabel}>{card.label}</span>
                        </article>
                      ))}
                    </div>

                    <div className={styles.mi7TeaserFooter}>
                      <section className={styles.mi7ContactPanel}>
                        <h3 className={styles.mi7PanelTitle}>Contatos diretos da Mi7</h3>
                        <div className={styles.mi7ContactGrid}>
                          {mi7Contacts.map(contact => (
                            <article key={contact.label} className={styles.mi7ContactItem}>
                              <span className={styles.mi7ContactIcon}>
                                <Mi7ContactIcon kind={contact.kind} />
                              </span>
                              <div>
                                <span className={styles.mi7ContactLabel}>{contact.label}</span>
                                <p className={styles.mi7ContactValue}>{contact.value}</p>
                              </div>
                            </article>
                          ))}
                        </div>
                      </section>

                      <div className={styles.mi7TeaserCta}>
                        <button
                          type="button"
                          className={`btn-cta ${styles.mi7PartnershipCta}`}
                          onClick={() => setIsMi7ModalOpen(true)}
                        >
                          Conheça os Detalhes da Parceria ABMAPRO × Mi7
                        </button>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>

            <footer className={styles.footer}>
              <div className={styles.footerInner}>
                <div>
                  <span className="microlabel">Fonte</span>
                  <p>{meta.source}</p>
                </div>
                <div>
                  <span className="microlabel">Publicado em</span>
                  <p>{new Date(meta.published_at).toLocaleDateString('pt-BR', { dateStyle: 'long' })}</p>
                </div>
              </div>
            </footer>
          </div>
        </main>
      </div>

      {isMi7ModalOpen && (
        <div
          className={styles.mi7ModalBackdrop}
          role="presentation"
          onClick={() => setIsMi7ModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mi7-modal-title"
            className={styles.mi7Modal}
            onClick={event => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.mi7ModalClose}
              onClick={() => setIsMi7ModalOpen(false)}
            >
              Fechar
            </button>

            <div className={styles.mi7ModalBody}>
              <span className={styles.mi7Eyebrow}>Parceria institucional</span>
              <h2 id="mi7-modal-title" className={styles.mi7Title}>
                A Parceria ABMAPRO & Grupo Mi7
              </h2>

              <p className={styles.mi7Lead}>
                A união entre ABMAPRO e Grupo Mi7 nasceu para suprir uma necessidade crítica
                do setor: criar indicadores confiáveis e padronizados que permitam compreender
                a evolução, a competitividade e a performance das Marcas Próprias no Brasil.
              </p>

              <section className={styles.mi7Panel}>
                <h3 className={styles.mi7PanelTitle}>Os 4 pilares da parceria</h3>
                <div className={styles.mi7PillarGrid}>
                  {mi7Objectives.map((objective, index) => (
                    <article key={objective} className={styles.mi7PillarCard}>
                      <span className={styles.mi7PillarIndex}>{index + 1}</span>
                      <h4 className={styles.mi7ObjectiveHeading}>{objective}</h4>
                    </article>
                  ))}
                </div>
              </section>

              <section className={styles.mi7ClosingPanel}>
                <p className={styles.mi7ClosingText}>
                  Com essa base metodológica e tecnológica, o INMP se consolida como uma
                  plataforma setorial de referência para leitura de sortimento, precificação e
                  inteligência competitiva em Marca Própria.
                </p>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
